import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Alert, Button, TextField, IconButton, Switch, FormControlLabel,
  Grid, Autocomplete, Divider, Chip, Snackbar
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

import { doc, onSnapshot, setDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';

import { VARSAYILAN_MOBIL_AYAR, SAYFA_TANIMLARI } from '../utils/mobilVarsayilanlar';

/**
 * SARRAF > MOBİL UYGULAMA
 *
 * Sarraf mobil uygulamasının birim listelerini, fiyat API adresini ve sol
 * menü içeriklerini yönetir. Tek bir Firestore dokümanına yazar:
 *   ayarlar/mobil
 *
 * Mobil uygulama bu dokümanı canlı dinler. Burada yapılan değişiklik
 * mağazaya yeni sürüm göndermeden telefonlara yansır.
 *
 * ÖNEMLİ: Mobil tarafta her alan için koda gömülü bir varsayılan vardır.
 * Bu doküman silinse veya bozulsa bile uygulama çalışmaya devam eder.
 */
export default function MobilUygulama() {
  const [ayar, setAyar] = useState(null);       // Firestore'daki hali
  const [taslak, setTaslak] = useState(null);   // Ekranda düzenlenen hali
  const [formuller, setFormuller] = useState([]);
  const [aktifSekme, setAktifSekme] = useState(0);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [bildirim, setBildirim] = useState('');

  // --- VERİ OKUMA -------------------------------------------------------

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'ayarlar', 'mobil'),
      (snap) => {
        const veri = snap.exists() ? snap.data() : null;
        setAyar(veri);
        setTaslak((onceki) => onceki ?? (veri || VARSAYILAN_MOBIL_AYAR));
      },
      () => {
        setAyar(null);
        setTaslak((onceki) => onceki ?? VARSAYILAN_MOBIL_AYAR);
      }
    );
    return () => unsub();
  }, []);

  // Birim seçiminde yanlış webKey yazılmasın diye formüller listesinden seçtiriyoruz.
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'formuller'), (snap) => {
      const liste = snap.docs
        .map((d) => (d.data()?.ad || '').toString().trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'tr'));
      setFormuller([...new Set(liste)]);
    });
    return () => unsub();
  }, []);

  // Doküman hiç yoksa her zaman kaydedilecek bir şey vardır (ilk oluşturma).
  // Varsa, yalnızca taslak farklıysa kaydet butonu açılır.
  const degisiklikVar = useMemo(
    () => !ayar || JSON.stringify(ayar) !== JSON.stringify(taslak),
    [ayar, taslak]
  );

  // --- DÜZENLEME YARDIMCILARI ------------------------------------------

  const sayfayiGuncelle = useCallback((anahtar, yama) => {
    setTaslak((t) => ({
      ...t,
      sayfalar: {
        ...t.sayfalar,
        [anahtar]: { ...(t.sayfalar?.[anahtar] || {}), ...yama }
      }
    }));
  }, []);

  const birimleriAl = (anahtar) =>
    anahtar === 'hesapBirimleri'
      ? (taslak?.hesapBirimleri || [])
      : (taslak?.sayfalar?.[anahtar]?.birimler || []);

  const birimleriYaz = (anahtar, yeni) => {
    if (anahtar === 'hesapBirimleri') {
      setTaslak((t) => ({ ...t, hesapBirimleri: yeni }));
    } else {
      sayfayiGuncelle(anahtar, { birimler: yeni });
    }
  };

  const birimEkle = (anahtar) => {
    const yeni = [...birimleriAl(anahtar), { unit: '', webKey: '' }];
    birimleriYaz(anahtar, yeni);
  };

  const birimSil = (anahtar, index) => {
    const yeni = birimleriAl(anahtar).filter((_, i) => i !== index);
    birimleriYaz(anahtar, yeni);
  };

  const birimTasi = (anahtar, index, yon) => {
    const liste = [...birimleriAl(anahtar)];
    const hedef = index + yon;
    if (hedef < 0 || hedef >= liste.length) return;
    [liste[index], liste[hedef]] = [liste[hedef], liste[index]];
    birimleriYaz(anahtar, liste);
  };

  const birimAlanDegistir = (anahtar, index, alan, deger) => {
    const liste = [...birimleriAl(anahtar)];
    liste[index] = { ...liste[index], [alan]: deger };
    birimleriYaz(anahtar, liste);
  };

  // --- MENÜ LİSTELERİ (mağazalar / iletişim) ----------------------------

  const menuListesi = (anahtar, alan) => taslak?.menu?.[anahtar]?.[alan] || [];

  const menuListesiYaz = (anahtar, alan, yeni) => {
    setTaslak((t) => ({
      ...t,
      menu: {
        ...t.menu,
        [anahtar]: { ...(t.menu?.[anahtar] || {}), [alan]: yeni }
      }
    }));
  };

  const menuSatirDegistir = (anahtar, alan, index, kolon, deger) => {
    const liste = [...menuListesi(anahtar, alan)];
    liste[index] = { ...liste[index], [kolon]: deger };
    menuListesiYaz(anahtar, alan, liste);
  };

  const menuSatirEkle = (anahtar, alan, bos) =>
    menuListesiYaz(anahtar, alan, [...menuListesi(anahtar, alan), bos]);

  const menuSatirSil = (anahtar, alan, index) =>
    menuListesiYaz(
      anahtar, alan,
      menuListesi(anahtar, alan).filter((_, i) => i !== index)
    );

  const menuSatirTasi = (anahtar, alan, index, yon) => {
    const liste = [...menuListesi(anahtar, alan)];
    const hedef = index + yon;
    if (hedef < 0 || hedef >= liste.length) return;
    [liste[index], liste[hedef]] = [liste[hedef], liste[index]];
    menuListesiYaz(anahtar, alan, liste);
  };

  // --- KAYDETME ---------------------------------------------------------

  const kaydet = async () => {
    setKaydediliyor(true);
    try {
      await setDoc(doc(db, 'ayarlar', 'mobil'), taslak, { merge: false });
      setBildirim('Kaydedildi. Telefonlara birkaç saniye içinde yansır.');
    } catch (e) {
      setBildirim('Kaydedilemedi: ' + (e?.message || 'bilinmeyen hata'));
    }
    setKaydediliyor(false);
  };

  const varsayilanaDon = () => {
    setTaslak(JSON.parse(JSON.stringify(VARSAYILAN_MOBIL_AYAR)));
    setBildirim('Uygulamadaki mevcut listeler forma yüklendi. Kaydetmeden etkili olmaz.');
  };

  if (!taslak) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Yükleniyor…</Typography>
      </Box>
    );
  }

  // --- GÖRÜNÜM ----------------------------------------------------------

  const aktif = SAYFA_TANIMLARI[aktifSekme];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <PhoneIphoneIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Mobil Uygulama</Typography>
        {!ayar && <Chip size="small" color="warning" label="Henüz kaydedilmedi" />}
        {degisiklikVar && <Chip size="small" color="info" label="Kaydedilmemiş değişiklik var" />}
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Buradaki değişiklikler <strong>mağazaya yeni sürüm göndermeden</strong> Sarraf
        mobil uygulamasına yansır. Uygulama bu ayarları canlı dinler.
        Bir alan boş bırakılırsa uygulama o alan için kendi varsayılanını kullanır,
        yani ekran hiçbir durumda boş kalmaz.
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Fiyat API adresi
        </Typography>
        <TextField
          fullWidth size="small"
          value={taslak.apiUrl || ''}
          onChange={(e) => setTaslak((t) => ({ ...t, apiUrl: e.target.value }))}
          placeholder="https://ukba-kasa.vercel.app/api/prices"
          helperText="https ile başlamalı. Geçersiz bir adres girilirse uygulama koddaki yedek adresi kullanır."
        />
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={aktifSekme}
          onChange={(_, v) => setAktifSekme(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {SAYFA_TANIMLARI.map((s) => <Tab key={s.anahtar} label={s.baslik} />)}
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {aktif.tip === 'liste' && (
          <>
            {aktif.anahtar !== 'hesapBirimleri' && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small" label="Sayfa başlığı"
                    value={taslak.sayfalar?.[aktif.anahtar]?.baslik || ''}
                    onChange={(e) => sayfayiGuncelle(aktif.anahtar, { baslik: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={taslak.sayfalar?.[aktif.anahtar]?.gorunur !== false}
                        onChange={(e) => sayfayiGuncelle(aktif.anahtar, { gorunur: e.target.checked })}
                      />
                    }
                    label="Uygulamada görünsün"
                  />
                </Grid>
              </Grid>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Birimler ({birimleriAl(aktif.anahtar).length})
              </Typography>
              <Button size="small" startIcon={<AddCircleIcon />} onClick={() => birimEkle(aktif.anahtar)}>
                Birim ekle
              </Button>
            </Box>

            <TableContainer sx={{ maxHeight: 460 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={56}>Sıra</TableCell>
                    <TableCell>Uygulamada görünen ad</TableCell>
                    <TableCell>Fiyat kaynağı (formül adı)</TableCell>
                    <TableCell width={120} align="right">İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {birimleriAl(aktif.anahtar).map((satir, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth size="small" variant="standard"
                          value={satir.unit ?? satir.longUnit ?? ''}
                          onChange={(e) =>
                            birimAlanDegistir(
                              aktif.anahtar, i,
                              satir.longUnit !== undefined ? 'longUnit' : 'unit',
                              e.target.value
                            )
                          }
                        />
                        {satir.shortUnit !== undefined && (
                          <TextField
                            fullWidth size="small" variant="standard" sx={{ mt: 0.5 }}
                            label="Kısa kod" value={satir.shortUnit || ''}
                            onChange={(e) => birimAlanDegistir(aktif.anahtar, i, 'shortUnit', e.target.value)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          freeSolo size="small" options={formuller}
                          value={satir.webKey || ''}
                          onChange={(_, v) => birimAlanDegistir(aktif.anahtar, i, 'webKey', v || '')}
                          onInputChange={(_, v) => birimAlanDegistir(aktif.anahtar, i, 'webKey', v || '')}
                          renderInput={(params) => (
                            <TextField {...params} variant="standard"
                              error={!!satir.webKey && !formuller.includes(satir.webKey)}
                              helperText={
                                satir.webKey && !formuller.includes(satir.webKey)
                                  ? 'Bu ad formüller listesinde yok — fiyat gelmeyebilir'
                                  : ' '
                              }
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => birimTasi(aktif.anahtar, i, -1)} disabled={i === 0}>
                          <ArrowUpwardIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton size="small" onClick={() => birimTasi(aktif.anahtar, i, 1)}
                          disabled={i === birimleriAl(aktif.anahtar).length - 1}>
                          <ArrowDownwardIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => birimSil(aktif.anahtar, i)}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {birimleriAl(aktif.anahtar).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Alert severity="warning" sx={{ my: 1 }}>
                          Liste boş. Bu haliyle kaydedilirse uygulama bu sayfa için
                          kendi varsayılan listesini gösterir.
                        </Alert>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {aktif.tip === 'magazalar' && (
          <>
            <TextField
              fullWidth size="small" label="Sayfa başlığı" sx={{ mb: 2 }}
              value={taslak.menu?.magazalar?.baslik || ''}
              onChange={(e) =>
                setTaslak((t) => ({
                  ...t,
                  menu: { ...t.menu, magazalar: { ...(t.menu?.magazalar || {}), baslik: e.target.value } }
                }))
              }
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Şubeler ({menuListesi('magazalar', 'subeler').length})
              </Typography>
              <Button size="small" startIcon={<AddCircleIcon />}
                onClick={() => menuSatirEkle('magazalar', 'subeler', { baslik: 'ŞUBE', adres: '', telefon: '' })}>
                Şube ekle
              </Button>
            </Box>
            {menuListesi('magazalar', 'subeler').map((sube, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                <Grid container spacing={1.5} alignItems="flex-start">
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Başlık" value={sube.baslik || ''}
                      onChange={(e) => menuSatirDegistir('magazalar', 'subeler', i, 'baslik', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField fullWidth size="small" label="Adres" multiline minRows={2}
                      value={sube.adres || ''}
                      helperText="Satır atlatmak için Enter kullanın"
                      onChange={(e) => menuSatirDegistir('magazalar', 'subeler', i, 'adres', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Telefon" value={sube.telefon || ''}
                      onChange={(e) => menuSatirDegistir('magazalar', 'subeler', i, 'telefon', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton size="small" onClick={() => menuSatirTasi('magazalar', 'subeler', i, -1)} disabled={i === 0}>
                      <ArrowUpwardIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton size="small" onClick={() => menuSatirTasi('magazalar', 'subeler', i, 1)}
                      disabled={i === menuListesi('magazalar', 'subeler').length - 1}>
                      <ArrowDownwardIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => menuSatirSil('magazalar', 'subeler', i)}>
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            {menuListesi('magazalar', 'subeler').length === 0 && (
              <Alert severity="warning">Liste boş. Bu haliyle kaydedilirse uygulama kendi varsayılan şubelerini gösterir.</Alert>
            )}
          </>
        )}

        {aktif.tip === 'iletisim' && (
          <>
            {[
              { alan: 'sosyal', baslik: 'Bizi Takip Edin' },
              { alan: 'iletisim', baslik: 'Bize Ulaşın' }
            ].map(({ alan, baslik }) => (
              <Box key={alan} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {baslik} ({menuListesi('iletisim', alan).length})
                  </Typography>
                  <Button size="small" startIcon={<AddCircleIcon />}
                    onClick={() => menuSatirEkle('iletisim', alan, { tip: 'web', etiket: '', deger: '' })}>
                    Bağlantı ekle
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={140}>Tür</TableCell>
                        <TableCell>Görünen yazı</TableCell>
                        <TableCell>Adres / numara</TableCell>
                        <TableCell width={110} align="right">İşlem</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {menuListesi('iletisim', alan).map((b, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Autocomplete
                              size="small" disableClearable
                              options={['instagram', 'youtube', 'facebook', 'twitter', 'web', 'telefon', 'whatsapp', 'eposta']}
                              value={b.tip || 'web'}
                              onChange={(_, v) => menuSatirDegistir('iletisim', alan, i, 'tip', v || 'web')}
                              renderInput={(params) => <TextField {...params} variant="standard" />}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField fullWidth size="small" variant="standard" value={b.etiket || ''}
                              onChange={(e) => menuSatirDegistir('iletisim', alan, i, 'etiket', e.target.value)} />
                          </TableCell>
                          <TableCell>
                            <TextField fullWidth size="small" variant="standard" value={b.deger || ''}
                              onChange={(e) => menuSatirDegistir('iletisim', alan, i, 'deger', e.target.value)}
                              helperText={
                                b.tip === 'telefon' ? 'Sadece rakam: 02123023444'
                                  : b.tip === 'eposta' ? 'ornek@ukbagold.com'
                                  : 'https:// ile başlayan adres'
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => menuSatirTasi('iletisim', alan, i, -1)} disabled={i === 0}>
                              <ArrowUpwardIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton size="small" onClick={() => menuSatirTasi('iletisim', alan, i, 1)}
                              disabled={i === menuListesi('iletisim', alan).length - 1}>
                              <ArrowDownwardIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => menuSatirSil('iletisim', alan, i)}>
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </>
        )}

        {aktif.tip === 'metin' && (
          <>
            <TextField
              fullWidth size="small" label="Başlık" sx={{ mb: 2 }}
              value={taslak.menu?.[aktif.anahtar]?.baslik || ''}
              onChange={(e) =>
                setTaslak((t) => ({
                  ...t,
                  menu: { ...t.menu, [aktif.anahtar]: { ...(t.menu?.[aktif.anahtar] || {}), baslik: e.target.value } }
                }))
              }
            />
            <TextField
              fullWidth multiline minRows={14} label="Metin"
              value={taslak.menu?.[aktif.anahtar]?.metin || ''}
              onChange={(e) =>
                setTaslak((t) => ({
                  ...t,
                  menu: { ...t.menu, [aktif.anahtar]: { ...(t.menu?.[aktif.anahtar] || {}), metin: e.target.value } }
                }))
              }
              helperText="Paragrafları boş satırla ayırın. Boş bırakılırsa uygulama kendi metnini gösterir."
            />
          </>
        )}
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained" startIcon={<SaveIcon />}
          onClick={kaydet} disabled={kaydediliyor || !degisiklikVar}
        >
          {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet ve yayınla'}
        </Button>
        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={varsayilanaDon}>
          Uygulamadaki mevcut listeleri yükle
        </Button>
      </Box>

      <Snackbar
        open={!!bildirim} autoHideDuration={5000}
        onClose={() => setBildirim('')} message={bildirim}
      />
    </Box>
  );
}
